import { DatePipe } from "@angular/common";
import { Component, DestroyRef, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { EmailModel } from "@models/email-templates/email-model";
import { EmailRecord } from "@models/email-templates/email-record";
import { EmailTemplatesHelper } from "@models/email-templates/email-templates-helper";
import { EmailStatus } from '@models/outgoing-messages/email-status.enum';
import { CommonOffcanvasComponent } from "@shared/components/commons/common-offcanvas/common-offcanvas.component";
import {
  OffcanvasPaginationTitleComponent
} from "@shared/components/commons/common-offcanvas/components/offcanvas-pagination-title/offcanvas-pagination-title.component";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { FileLabelComponent } from "@shared/components/commons/file-label/file-label.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import {
  ShadowDomViewerComponent
} from "@shared/components/email-preview/shadow-dom-viewer/shadow-dom-viewer.component";
import { HasRoleDirective } from "@shared/directives/has-role.directive";
import { EmailsApiService } from "@shared/services/emails-api.service";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import { ToastrService } from "ngx-toastr";
import { catchError, EMPTY, finalize, of, tap } from "rxjs";
import {
  AgOutgoingEmailMessageStatusComponent
} from "../ag-outgoing-message-status/ag-outgoing-email-message-status.component";
import { EmailRecipientsComponent } from "../email-recipients/email-recipients.component";

@Component({
  selector: 'app-email-details',
  standalone: true,
  imports: [
    CommonOffcanvasComponent,
    LoaderComponent,
    DisclaimerComponent,
    DatePipe,
    AgOutgoingEmailMessageStatusComponent,
    EmailRecipientsComponent,
    ShadowDomViewerComponent,
    NgxSkeletonLoaderModule,
    HasRoleDirective,
    FileLabelComponent,
    OffcanvasPaginationTitleComponent
  ],
  templateUrl: 'outgoing-email-message-details.component.html',
  styleUrls: [
    'outgoing-email-message-details.component.scss',
    '../../../../../../shared/components/email-preview/email-preview.component.scss',
  ]
})
export class OutgoingEmailMessageDetailsComponent extends CommonOffcanvasComponent {
  private toastr = inject(ToastrService);
  private emailTemplatesService = inject(EmailsApiService);
  private sanitizer = inject(DomSanitizer);
  private destroyRef = inject(DestroyRef);

  @Input({ alias: 'emailRecord', required: true }) set emailRecordSetter(record: EmailRecord | null) {
    this.emailRecord = record;
    this.emailModel = null;
    this.htmlContent.set(null);

    if (record) {
      this.loadEmailModel(record);
    }
  }

  @Input() totalRows = 0;
  @Input() currentEmailMessageIndex = 0;
  @Input() currentEmailMessageItemNumber = 0;
  @Input() isOuterLoading = false;

  @Output() getNextEmailMessage = new EventEmitter<void>();
  @Output() getPreviousEmailMessage = new EventEmitter<void>();
  @Output() refreshData = new EventEmitter<void>();

  readonly customStyle = `.container {margin: 0 !important;}`;
  readonly EmailStatus = EmailStatus;
  htmlContent = signal<SafeHtml | null>(null);
  emailRecord: EmailRecord | null = null;
  emailModel: EmailModel | null = null;
  isEmailModelLoading = false;
  isMessageActionLoading = false;
  resentEmailDate: Date | null = null;
  showBodyWarning = false;
  bodywarningMessage = 'Your email content will be available shortly. Please check back in a few minutes.'

  openNextEmail(): void {
    this.getNextEmailMessage.emit();
  }

  openPreviousEmail(): void {
    this.getPreviousEmailMessage.emit();
  }

  deleteEmail(): void {
    // available only for admins
    if (!this.emailRecord || !confirm('Deleting the email will not cancel pending eSigning envelopes or recall sent emails. To cancel an eSigning request, you must void the envelope. Do you want to continue?')) return;
    this.isMessageActionLoading = true;
    this.emailTemplatesService.deleteOutgoingEmailMessage(this.emailRecord.emailMessageId)
      .pipe(finalize(() => this.isMessageActionLoading = false))
      .subscribe({
        next: () => {
          this.refreshData.emit();
          this.closeOffcanvas();
        },
        error: () => this.toastr.error('Something happened while deleting email', 'Error')
      });
  }

  resendEmail(): void {
    if (this.emailRecord?.status !== EmailStatus.Failed || !this.emailModel) return;
    this.isMessageActionLoading = true;

    this.emailTemplatesService.resendOutgoingEmailMessage(this.emailRecord.emailMessageId)
      .pipe(finalize(() => this.isMessageActionLoading = false))
      .subscribe({
        next: () => {
          this.refreshData.emit();
          this.resentEmailDate = new Date();
        },
        error: () => this.toastr.error('Something happened while sending email', 'Error')
      });
  }

  private loadEmailModel(record: EmailRecord): void {
    this.isEmailModelLoading = true;

    this.emailTemplatesService.getEmailDetails(record.emailMessageId)
      .pipe(
        tap((emailMessageModel) => {
          if (emailMessageModel.html == null || emailMessageModel.html == '') {
            this.showBodyWarning = true;
          }
          else {
            this.showBodyWarning = false;
          }
        }),
        catchError(() => {
          this.toastr.error('Error happened while loading email', 'Error');
          this.showBodyWarning = false;
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isEmailModelLoading = false)
      )
      .subscribe({
        next: (emailMessageModel) => {
          if (emailMessageModel.emailMessageId !== this.emailRecord?.emailMessageId) return;
          this.emailModel = emailMessageModel;
          this.htmlContent.set(emailMessageModel.html ? this.sanitizer.bypassSecurityTrustHtml(EmailTemplatesHelper.replaceNewLine(emailMessageModel.html)) : '');
        },
        error: () => this.toastr.error('Something happened while loading email', 'Error')
      });
  }

  downloadFile(fileName: string) {
    this.emailTemplatesService.downloadAttachment(this.emailModel!.emailMessageId, fileName).pipe(
      catchError(() => {
        this.toastr.error(`Download ${fileName} error`, "Error");
        return of(null);
      }),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe({
      next: (res) => {
        if (!res)
          return;

        const url = window.URL.createObjectURL(res);

        // 2. Create a temporary <a> and trigger the download
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;       // the filename the user sees
        document.body.appendChild(a);
        a.click();

        // 3. Clean up
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: () => this.toastr.error('Something happened while downloading email file', 'Error')
    });
  }
}
